import { useState, useEffect } from 'react'
import { VoiceBot } from './components'

const VOICE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'alloy', label: 'Alloy ♀' },
  { value: 'ash', label: 'Ash ♂' },
  { value: 'ballad', label: 'Ballad ♀' },
  { value: 'cedar', label: 'Cedar ♂ ⭐' },
  { value: 'coral', label: 'Coral ♀' },
  { value: 'echo', label: 'Echo ♂' },
  { value: 'marin', label: 'Marin ♀ ⭐' },
  { value: 'sage', label: 'Sage ♀' },
  { value: 'shimmer', label: 'Shimmer ♀' },
  { value: 'verse', label: 'Verse ♂' },
] as const;

// Caller-transcript (STT) model A/B picker options. Writes to localStorage
// (ilandsTranscribeModel); the widget reads it at voice-session start.
// Empty = production default (gpt-realtime-whisper).
const TRANSCRIBE_MODEL_OPTIONS = [
  { value: '', label: 'STT: gpt-realtime-whisper (default)' },
  { value: 'gpt-4o-transcribe', label: 'STT: gpt-4o-transcribe (rollback)' },
] as const;

interface DemoCustomer {
  id: string;
  publicKey: string;
  name: string;
  primaryColor: string;
  iconUrl: string | null;
}

function App() {
  const [customer, setCustomer] = useState<DemoCustomer | null>(null);
  const [voiceOverride, setVoiceOverride] = useState<string | null>(null);
  // Caller-transcript model A/B override (demo only), persisted to localStorage.
  const [transcribeModel, setTranscribeModel] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('ilandsTranscribeModel') || '';
      // Drop stale or unverified values so they can't disable caller captions.
      if (!TRANSCRIBE_MODEL_OPTIONS.some((o) => o.value === saved)) {
        localStorage.removeItem('ilandsTranscribeModel');
        return '';
      }
      return saved;
    } catch { return ''; }
  });
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Fetch the selected customer from session
  useEffect(() => {
    async function loadCustomer() {
      try {
        const response = await fetch('/api/demo-customer');
        const data = await response.json();
        
        if (data.success && data.customer) {
          setCustomer(data.customer);
          if (data.voiceOverride) {
            setVoiceOverride(data.voiceOverride);
            console.log('🎤 Voice override active:', data.voiceOverride);
          }
        } else {
          setAuthError('Session missing or invalid. Please sign in again.');
        }
      } catch (error) {
        console.error('Failed to load demo customer:', error);
        setAuthError('Could not load demo session. Please sign in again.');
      } finally {
        setLoading(false);
      }
    }
    
    loadCustomer();
  }, []);

  const handleLogout = () => {
    window.location.href = '/api/demo-logout';
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 50%, #FFE8A3 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <p style={{ color: '#666' }}>Loading...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 50%, #FFE8A3 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: 24,
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            maxWidth: 420,
            width: '100%',
            padding: 24,
            textAlign: 'center',
            direction: 'rtl',
          }}
        >
          <h2 style={{ margin: '0 0 10px', color: '#1f2937' }}>שגיאת התחברות</h2>
          <p style={{ margin: '0 0 18px', color: '#6b7280' }}>
            {authError || 'לא נמצאה סשן תקין. אנא התחברו מחדש.'}
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.href = '/api/demo-logout';
            }}
            style={{
              border: 'none',
              background: '#FF6B5B',
              color: 'white',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            חזרה להתחברות
          </button>
        </div>
      </div>
    );
  }

  const publicKey = customer.publicKey;
  const iconUrl = customer.iconUrl || undefined;
  const customerName = customer.name;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 50%, #FFE8A3 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header with customer name, voice override indicator, and logout */}
      <div
        style={{
          position: 'fixed',
          top: 16,
          left: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          zIndex: 9999,
        }}
      >
        <span
          style={{
            padding: '8px 16px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 8,
            fontSize: 14,
            color: '#333',
            fontWeight: 500,
          }}
        >
          🎯 {customerName}
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 8,
          }}
        >
          <span style={{ fontSize: 14, color: '#666' }}>🎤</span>
          <select
            value={voiceOverride || ''}
            onChange={(e) => setVoiceOverride(e.target.value || null)}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14,
              color: '#333',
              background: 'white',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {VOICE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 8,
          }}
          title="Caller transcript (STT) model — applies to the NEXT call"
        >
          <span style={{ fontSize: 14, color: '#666' }}>📝</span>
          <select
            value={transcribeModel}
            onChange={(e) => {
              const v = e.target.value;
              setTranscribeModel(v);
              try {
                if (v) localStorage.setItem('ilandsTranscribeModel', v);
                else localStorage.removeItem('ilandsTranscribeModel');
              } catch { /* noop */ }
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: 4,
              fontSize: 14,
              color: '#333',
              background: 'white',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {TRANSCRIBE_MODEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          style={{
            padding: '8px 16px',
            background: 'rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)')}
          title="Log out"
        >
          <span>Log out</span>
        </button>
      </div>

      {/* Voice Bot Widget */}
      <VoiceBot
        config={{
          publicKey: publicKey,
          buttonPosition: { bottom: 24, left: 24 },
          ...(iconUrl ? { iconUrl } : {}),
          // Voice override from demo login (only applies to demo site, not embedded widgets)
          ...(voiceOverride
            ? {
                voice: voiceOverride as 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'sage' | 'shimmer' | 'verse' | 'marin' | 'cedar',
                forceVoiceOverride: true,
              }
            : {}),
        }}
      />
    </div>
  )
}

export default App
